import React, { useEffect, useState } from "react";
import { FaChevronLeft, FaChevronRight, FaPen, FaTrash } from "react-icons/fa";

import { EmptyState } from "components/admin/ui";

type RowData = object;

interface ColumnDefinition<T extends RowData> {
  label: string;
  accessor: string | ((row: T) => React.ReactNode);
  className?: string;
}

interface DataTableProps<T extends RowData> {
  data: T[];
  columns?: ColumnDefinition<T>[];
  title?: string;
  onEdit?: (row: T) => void;
  onView?: (value: React.Key, row?: T) => void;
  onRemove?: (value: React.Key, row?: T) => void;
  isAdmin?: boolean;
  currentPage?: number;
  pageSize?: number;
  totalItems?: number;
  onPageChange?: (pageIndex: number) => void;
  onPageSizeChange?: (pageSize: number) => void;
  headerTranslations?: Record<string, string>;
  specificHeaders?: string[];
  canView?: boolean;
  canEdit?: boolean;
  canRemove?: boolean;
  isLoading?: boolean;
  hideNav?: boolean;
  pageSizeOptions?: number[];
  defaultPageSize?: number;
  emptyTitle?: string;
  emptyDescription?: string;
  rowKey?: string | ((row: T, index: number) => React.Key);
  toolbar?: React.ReactNode;
  loadingState?: React.ReactNode;
  rowClickable?: boolean;
}

const Loader = () => (
  <div className="flex items-center justify-center py-14">
    <div className="h-12 w-12 animate-spin rounded-full border-4 border-[var(--brand-accent)]/20 border-t-[var(--brand-accent)]" />
  </div>
);

const ToolbarSurface: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <div className="overflow-hidden rounded-[24px] border border-[var(--brand-border)] bg-[rgba(255,255,255,0.04)] shadow-[0_16px_24px_rgba(0,0,0,0.18)]">
    <div className="border-b border-[var(--brand-border)] px-4 py-3">
      <p className="font-anonymous text-xs uppercase tracking-[0.22em] text-[var(--brand-highlight)]">
        Context
      </p>
      <p className="mt-1 text-sm leading-6 text-[var(--brand-muted)]">
        Focus the records below before reviewing or making changes.
      </p>
    </div>
    <div className="p-4">{children}</div>
  </div>
);

const renderDisplayValue = (value: React.ReactNode) =>
  React.isValidElement(value) ? value : value?.toString() || "N/A";

const handleRowViewKeyDown = (event: React.KeyboardEvent, onActivate: () => void) => {
  if (event.key === "Enter" || event.key === " ") {
    event.preventDefault();
    onActivate();
  }
};

export const DataTable = <T extends RowData>({
  data = [],
  onEdit,
  onView,
  onRemove,
  columns,
  currentPage = 1,
  pageSize,
  totalItems = 0,
  onPageChange,
  onPageSizeChange,
  headerTranslations = {},
  specificHeaders = [],
  canView = true,
  canEdit = true,
  canRemove = true,
  isLoading = false,
  hideNav = false,
  pageSizeOptions = [6, 10, 16, 24],
  defaultPageSize = 10,
  emptyTitle = "No records found",
  emptyDescription = "Try widening the filters or add a new record to populate this view.",
  rowKey = "id",
  toolbar,
  loadingState,
  rowClickable = true,
}: DataTableProps<T>) => {
  const resolvedColumns =
    columns ??
    (specificHeaders.length > 0
      ? specificHeaders.map((header) => ({
          label: headerTranslations[header] || header,
          accessor: header,
        }))
      : data.length > 0
        ? Object.keys(data[0] as Record<string, unknown>).map((header) => ({
            label: headerTranslations[header] || header,
            accessor: header,
          }))
        : []);

  const [currentPageIndex, setCurrentPageIndex] = useState(Math.max(currentPage - 1, 0));
  const [itemsPerPage, setItemsPerPage] = useState(pageSize || defaultPageSize);

  useEffect(() => {
    setCurrentPageIndex(Math.max(currentPage - 1, 0));
  }, [currentPage]);

  useEffect(() => {
    setItemsPerPage(pageSize || defaultPageSize);
  }, [defaultPageSize, pageSize]);

  useEffect(() => {
    if (onPageChange) {
      onPageChange(currentPageIndex + 1);
    }
  }, [currentPageIndex, onPageChange]);

  useEffect(() => {
    if (onPageSizeChange) {
      onPageSizeChange(itemsPerPage);
    }
  }, [itemsPerPage, onPageSizeChange]);

  const totalRows = totalItems || data.length;
  const totalPages = Math.max(Math.ceil(totalRows / itemsPerPage), 1);

  useEffect(() => {
    if (currentPageIndex > totalPages - 1) {
      setCurrentPageIndex(Math.max(totalPages - 1, 0));
    }
  }, [currentPageIndex, totalPages]);

  const paginatedData = data.slice(
    currentPageIndex * itemsPerPage,
    currentPageIndex * itemsPerPage + itemsPerPage,
  );

  const resolveActionValue = (item: T, index: number): React.Key => {
    if (typeof rowKey === "function") {
      return rowKey(item, index);
    }

    const itemRecord = item as Record<string, unknown>;

    if (typeof rowKey === "string" && itemRecord[rowKey] !== undefined) {
      return itemRecord[rowKey] as React.Key;
    }

    return (itemRecord.id as React.Key | undefined) ?? index;
  };

  const handlePageChange = (index: number) => {
    if (index >= 0 && index < totalPages) {
      setCurrentPageIndex(index);
    }
  };

  const isRowClickable = Boolean(canView && onView && rowClickable);
  const hasEditAction = Boolean(canEdit && onEdit);
  const hasRemoveAction = Boolean(canRemove && onRemove);
  const showActionsColumn = hasEditAction || hasRemoveAction;

  if (isLoading) {
    return (
      <div className="space-y-5">
        {toolbar ? <ToolbarSurface>{toolbar}</ToolbarSurface> : null}
        {loadingState || <Loader />}
      </div>
    );
  }

  return (
    <div className="space-y-5">
      {toolbar ? <ToolbarSurface>{toolbar}</ToolbarSurface> : null}

      {paginatedData.length === 0 ? (
        <div className="rounded-[24px] border border-[var(--brand-border)] bg-[rgba(255,255,255,0.04)] p-5">
          <EmptyState description={emptyDescription} title={emptyTitle} />
        </div>
      ) : (
        <div className="space-y-3 md:hidden">
          {paginatedData.map((item, index) => {
            const itemRecord = item as Record<string, unknown>;
            const resolvedKey =
              typeof rowKey === "function"
                ? rowKey(item, index)
                : ((itemRecord[rowKey] as React.Key | undefined) ?? index);
            const resolvedActionValue = resolveActionValue(item, index);
            const activateView = () => onView?.(resolvedActionValue, item);

            return (
              <div
                className={`rounded-[22px] border border-[var(--brand-border)] bg-[rgba(255,255,255,0.04)] p-4 shadow-[0_14px_24px_rgba(0,0,0,0.16)]${
                  isRowClickable ? " cursor-pointer transition hover:bg-[rgba(240,191,86,0.04)]" : ""
                }`}
                key={resolvedKey}
                onClick={isRowClickable ? activateView : undefined}
                onKeyDown={
                  isRowClickable ? (event) => handleRowViewKeyDown(event, activateView) : undefined
                }
                role={isRowClickable ? "button" : undefined}
                tabIndex={isRowClickable ? 0 : undefined}
                aria-label={isRowClickable ? "View record" : undefined}
              >
                <div className="space-y-3">
                  {resolvedColumns.map((column) => {
                    const value =
                      typeof column.accessor === "function"
                        ? column.accessor(item)
                        : (item as Record<string, unknown>)[column.accessor];

                    return (
                      <div
                        className="grid grid-cols-[92px_minmax(0,1fr)] gap-3"
                        key={`${resolvedKey}-${column.label}`}
                      >
                        <p className="text-xs font-semibold uppercase tracking-[0.14em] text-[var(--brand-muted)]">
                          {column.label}
                        </p>
                        <div className="min-w-0 break-words text-[13px] text-[var(--brand-ink)]">
                          {renderDisplayValue(value)}
                        </div>
                      </div>
                    );
                  })}

                  {showActionsColumn ? (
                    <div
                      className="flex flex-wrap items-center gap-2 border-t border-[var(--brand-border)] pt-3"
                      onClick={(event) => event.stopPropagation()}
                      onKeyDown={(event) => event.stopPropagation()}
                    >
                      {hasEditAction ? (
                        <button
                          className="inline-flex items-center gap-2 rounded-xl border border-[var(--brand-border)] px-3 py-2 text-[12px] text-[var(--brand-accent-deep)] transition hover:bg-[var(--brand-bg)]"
                          onClick={() => onEdit?.(item)}
                          type="button"
                        >
                          <FaPen size={11} />
                          Edit
                        </button>
                      ) : null}
                      {hasRemoveAction ? (
                        <button
                          className="inline-flex items-center gap-2 rounded-xl border border-[var(--brand-error-border)]/20 px-3 py-2 text-[12px] text-[var(--brand-error-text)] transition hover:bg-[var(--brand-error-ring)]"
                          onClick={() => onRemove?.(resolvedActionValue, item)}
                          type="button"
                        >
                          <FaTrash size={11} />
                          Remove
                        </button>
                      ) : null}
                    </div>
                  ) : null}
                </div>
              </div>
            );
          })}
        </div>
      )}

      <div className={`${paginatedData.length === 0 ? "hidden" : "hidden md:block"} overflow-hidden rounded-[24px] border border-[var(--brand-border)] bg-[rgba(255,255,255,0.04)]`}>
        <div className="overflow-x-auto">
          <table className="brand-table">
            <thead>
              <tr>
                {resolvedColumns.map((column) => (
                  <th key={column.label}>{column.label}</th>
                ))}
                {showActionsColumn ? <th className="w-[140px]">Actions</th> : null}
              </tr>
            </thead>
            <tbody>
              {paginatedData.map((item, index) => {
                const itemRecord = item as Record<string, unknown>;
                const resolvedKey =
                  typeof rowKey === "function"
                    ? rowKey(item, index)
                    : ((itemRecord[rowKey] as React.Key | undefined) ?? index);
                const resolvedActionValue = resolveActionValue(item, index);
                const activateView = () => onView?.(resolvedActionValue, item);

                return (
                  <tr
                    key={resolvedKey}
                    className={`bg-transparent transition hover:bg-[rgba(240,191,86,0.04)]${
                      isRowClickable ? " cursor-pointer" : ""
                    }`}
                    onClick={isRowClickable ? activateView : undefined}
                    onKeyDown={
                      isRowClickable
                        ? (event) => handleRowViewKeyDown(event, activateView)
                        : undefined
                    }
                    role={isRowClickable ? "button" : undefined}
                    tabIndex={isRowClickable ? 0 : undefined}
                    aria-label={isRowClickable ? "View record" : undefined}
                  >
                    {resolvedColumns.map((column) => {
                      const value =
                        typeof column.accessor === "function"
                          ? column.accessor(item)
                          : (item as Record<string, unknown>)[column.accessor];

                      return (
                        <td className={column.className} key={`${resolvedKey}-${column.label}`}>
                          {renderDisplayValue(value)}
                        </td>
                      );
                    })}
                    {showActionsColumn ? (
                      <td onClick={(event) => event.stopPropagation()}>
                        <div className="flex items-center gap-2">
                          {hasEditAction ? (
                            <button
                              className="rounded-xl border border-[var(--brand-border)] p-2 text-[var(--brand-accent-deep)] transition hover:bg-[var(--brand-bg)]"
                              onClick={() => onEdit?.(item)}
                              type="button"
                            >
                              <FaPen size={12} />
                            </button>
                          ) : null}
                          {hasRemoveAction ? (
                            <button
                              className="rounded-xl border border-[var(--brand-error-border)]/20 p-2 text-[var(--brand-error-text)] transition hover:bg-[var(--brand-error-ring)]"
                              onClick={() => onRemove?.(resolvedActionValue, item)}
                              type="button"
                            >
                              <FaTrash size={12} />
                            </button>
                          ) : null}
                        </div>
                      </td>
                    ) : null}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {!hideNav && paginatedData.length > 0 ? (
        <div className="flex flex-col gap-4 rounded-3xl border border-[var(--brand-border)] bg-[rgba(255,255,255,0.04)] px-4 py-4 text-[13px] text-[var(--brand-muted)] shadow-[0_16px_24px_rgba(0,0,0,0.18)] sm:flex-row sm:items-center sm:justify-between">
          <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:gap-3">
            <span>
              Showing {currentPageIndex * itemsPerPage + 1} to{" "}
              {Math.min((currentPageIndex + 1) * itemsPerPage, totalRows)} of {totalRows}
            </span>
            <div className="flex items-center gap-2">
              <span>Rows</span>
              <select
                className="brand-input w-24 px-3 py-2"
                onChange={(event) => {
                  setItemsPerPage(Number(event.target.value));
                  setCurrentPageIndex(0);
                }}
                value={itemsPerPage}
              >
                {pageSizeOptions.map((option) => (
                  <option key={option} value={option}>
                    {option}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div className="flex items-center gap-3 self-end sm:self-auto">
            <button
              className="flex h-10 w-10 items-center justify-center rounded-2xl border border-[var(--brand-border)] bg-[rgba(255,255,255,0.04)] text-[var(--brand-ink)] transition hover:border-[var(--brand-highlight)] hover:bg-[rgba(240,191,86,0.08)] disabled:cursor-not-allowed disabled:opacity-45"
              disabled={currentPageIndex === 0}
              onClick={() => handlePageChange(currentPageIndex - 1)}
              type="button"
            >
              <FaChevronLeft size={12} />
            </button>
            <span className="font-anonymous text-xs uppercase tracking-[0.18em] text-[var(--brand-muted)]">
              Page {currentPageIndex + 1} of {totalPages}
            </span>
            <button
              className="flex h-10 w-10 items-center justify-center rounded-2xl border border-[var(--brand-border)] bg-[rgba(255,255,255,0.04)] text-[var(--brand-ink)] transition hover:border-[var(--brand-highlight)] hover:bg-[rgba(240,191,86,0.08)] disabled:cursor-not-allowed disabled:opacity-45"
              disabled={currentPageIndex >= totalPages - 1}
              onClick={() => handlePageChange(currentPageIndex + 1)}
              type="button"
            >
              <FaChevronRight size={12} />
            </button>
          </div>
        </div>
      ) : null}
    </div>
  );
};
